<!-- src/lib/components/MpfFilesSummary/MpfFilesSummary.vue -->
<script lang="ts" setup>
  import { computed, onBeforeUnmount, ref, watch } from 'vue';

  type FileSummaryItem = {
    file: File;
    url?: string; // blob preview URL (optional)
  };

  const props = withDefaults(
    defineProps<{
      modelValue: File[] | null;
      allowDelete?: boolean;
      id?: string;
    }>(),
    {
      allowDelete: true,
      id: 'mpf-files-summary',
    },
  );

  const emit = defineEmits<{
    (e: 'update:modelValue', value: File[]): void;
    (e: 'remove', payload: { file: File; index: number }): void;
  }>();

  const items = ref<FileSummaryItem[]>([]);

  const files = computed<File[]>(() => props.modelValue ?? []);

  const bytesToMB = (bytes: number, decimals = 2) =>
    Number((bytes / 1024 / 1024).toFixed(decimals));

  function revokeAll() {
    for (const item of items.value) {
      if (item.url) URL.revokeObjectURL(item.url);
    }
  }

  function buildItems(nextFiles: File[]) {
    // rebuild items list; revoke previous URLs to avoid leaks
    revokeAll();

    items.value = nextFiles.map((file) => {
      return {
        file,
      };
    });
  }

  watch(
    files,
    (next) => {
      buildItems(next);
    },
    { immediate: true },
  );

  onBeforeUnmount(() => revokeAll());

  function removeFile(index: number) {
    const next = [...files.value];
    const removed = next.splice(index, 1)[0];

    emit('update:modelValue', next);
    if (removed) emit('remove', { file: removed, index });
  }

  function downloadFile(file: File) {
    const url = URL.createObjectURL(file);

    const a = document.createElement('a');
    a.href = url;
    a.download = file.name || 'download';
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();

    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
</script>

<template>
  <dl class="mpf-summary-list mpf-u-margin-bottom-4" v-if="items.length">
    <div
      class="mpf-summary-list__row"
      v-for="(item, index) in items"
      :key="`${item.file.name}-${item.file.size}-${index}`"
    >
      <dt class="mpf-summary-list__key">File {{ index + 1 }}</dt>

      <dd class="mpf-summary-list__value" data-private>
        <a class="mpf-link" href="#" @click.prevent="downloadFile(item.file)">
          {{ item.file.name }}</a
        >, {{ bytesToMB(item.file.size) }} mb
      </dd>

      <dd class="mpf-summary-list__actions" v-if="allowDelete">
        <a class="mpf-link" href="#" @click.prevent="removeFile(index)"
          >Delete<span class="mpf-visually-hidden">file {{ index + 1 }}</span></a
        >
      </dd>
    </div>
  </dl>
</template>

<style scoped>
  .mpf-files-summary__list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: grid;
    gap: 12px;
  }

  .mpf-files-summary__item {
    border: 1px solid;
    padding: 10px;
    border-radius: 8px;
  }

  .mpf-files-summary__row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
  }

  .mpf-files-summary__details {
    margin-top: 2px;
    font-size: 0.9em;
  }

  .mpf-files-summary__remove {
    border: 1px solid;
    background: transparent;
    padding: 6px 10px;
    border-radius: 6px;
    cursor: pointer;
    white-space: nowrap;
  }

  .mpf-files-summary__preview {
    margin-top: 10px;
  }

  .mpf-files-summary__preview-image {
    max-width: 240px;
    max-height: 120px;
    height: auto;
    display: block;
  }

  .mpf-files-summary__preview-pdf {
    width: 100%;
    max-width: 520px;
    height: 80px;
    border: 1px solid;
    border-radius: 6px;
  }

  .mpf-files-summary__empty {
    margin: 0;
  }
</style>
